import { SlideOutSheet } from './SlideOutSheet';

interface ModeledSavingsSlideOutProps {
  open: boolean;
  onClose: () => void;
  carrierName?: string;
  annualHealthcareInflationRate?: number;
}

export function ModeledSavingsSlideOut({
  open,
  onClose,
  carrierName,
  annualHealthcareInflationRate,
}: ModeledSavingsSlideOutProps) {
  // Use the passed inflation rate, or fall back to the shared constant
  const inflationRate = annualHealthcareInflationRate ?? 0.054; // Default 5.5% if not provided
  const inflationRatePercentage = (inflationRate * 100).toFixed(1);

  const isCareScout = carrierName === 'Care Assurance';

  const slideOutContent = isCareScout ? (
    <div className="flex flex-grow flex-col gap-6 pb-8">
      <div className="space-y-3">
        <h2 className="text-primary-dark text-lg font-semibold md:text-xl">
          About Modeled Savings
        </h2>
        <p className="text-primary-dark text-sm leading-relaxed md:text-base">
          This amount represents projected savings on your{' '}
          <span className="italic">out-of-pocket</span> expenses for the cost of home care services
          by utilizing the CareScout Quality Network (CQN) for home care. Savings are based on an
          estimated 11%<sup>1</sup> average savings rate compared to median costs of care (as of 10/1/2025) and
          are <span className="font-semibold">not guaranteed</span>. Network availability and actual
          savings may vary.
        </p>
      </div>

      <div className="space-y-3">
        <h2 className="text-primary-dark text-lg font-semibold md:text-xl">
          Understanding the CareScout Quality Network (CQN)
        </h2>
        <p className="text-primary-dark text-sm leading-relaxed md:text-base">
          The CareScout Care Assurance policy currently offers access to the CareScout Quality
          Network (CQN), managed by CareScout, LLC (an affiliate of CareScout Insurance Company).
          The CQN is a network of aging care providers that have been evaluated by CareScout and
          have agreed to provide services at preferred rates (discounts).
        </p>
      </div>

      <div className="space-y-3">
        <h2 className="text-primary-dark text-lg font-semibold md:text-xl">
          Network Structure and Benefits
        </h2>
        <p className="text-primary-dark text-sm leading-relaxed md:text-base">
          This feature is designed to connect policyholders with vetted providers at preferred
          rates.
        </p>
        <ul className="text-primary-dark ml-6 list-disc space-y-2 text-sm md:text-base">
          <li>
            <span className="font-semibold">Vetted Providers:</span> Providers in the CQN are
            evaluated against quality standards established by CareScout. This process includes a
            review of the provider's:
            <ul className="ml-6 mt-2 list-disc space-y-1">
              <li>Credentials (licensure, insurance, and regulatory compliance)</li>
              <li>
                Business practices (including staffing, training, and retention rates, often via
                survey)
              </li>
              <li>Financial health</li>
              <li>Commitment to person-centered care practices</li>
            </ul>
          </li>
          <li>
            <span className="font-semibold">Scope of Savings:</span> The preferred pricing applies
            to costs covered by the Care Assurance policy (potentially extending the duration of
            benefits) as well as to any out-of-pocket (OOP) expenses for home care services incurred
            by the policyholder. Note that preferred pricing may not apply to all charges associated
            with home care (for example, mileage fees).
          </li>
        </ul>
      </div>

      <div className="space-y-3">
        <h2 className="text-primary-dark text-lg font-semibold md:text-xl">
          How Waterlily Models the Financial Impact
        </h2>
        <p className="text-primary-dark text-sm leading-relaxed md:text-base">
          Waterlily accounts for the potential reduction in OOP expenses for home care services when
          calculating the projected Total Return on Investment (Total ROI) and Compound Annual
          Growth Rate (CAGR) for the Care Assurance product, effectively increasing the policy's
          return. These savings are identified by name in the long-term care financing plan metrics
          header.
        </p>
        <p className="text-primary-dark text-sm leading-relaxed md:text-base">
          This projection is a <span className="font-semibold">hypothetical demonstration</span> (a
          "Non-policy Benefit") based on the following assumptions:
        </p>
        <ol className="text-primary-dark ml-6 list-decimal space-y-2 text-sm md:text-base">
          <li>
            <span className="font-semibold">Network Utilization:</span> The model assumes you will
            find and use providers within the CQN for your projected OOP home care needs.
          </li>
          <li>
            <span className="font-semibold">Discount Rate:</span> The model assumes the costs for
            those providers will be <span className="font-semibold">11%<sup>1</sup></span> less than the
            projected median cost of home health care<sup>2</sup> in your ZIP code.
          </li>
          <li>
            <span className="font-semibold">Costs outside of the Network:</span> The model assumes that
            costs for home care services you would obtain without using the CQN would be at least equal
            to the projected median cost of home health care<sup>2</sup> in your ZIP code.
          </li>
        </ol>
      </div>

      <div className="space-y-3">
        <h2 className="text-primary-dark text-lg font-semibold md:text-xl">
          Limitations and Considerations
        </h2>
        <p className="text-primary-dark text-sm leading-relaxed md:text-base">
          The CQN is an actively growing network, but the projections rely on assumptions that may
          not reflect your actual experience:
        </p>
        <ul className="text-primary-dark ml-6 list-disc space-y-2 text-sm md:text-base">
          <li>
            <span className="font-semibold">Non-Guaranteed Benefit:</span> Access to the CQN is not
            guaranteed by the insurance contract. It is a separate feature that can be modified or
            discontinued by CareScout at any time.
          </li>
          <li>
            <span className="font-semibold">Actual Savings Will Vary:</span> The 11%<sup>1</sup> figure is a
            national average. Any actual savings will vary; they may be higher or lower depending on
            your location, the specific providers used, and future rate negotiations for providers in the CQN. The discount
            rate may increase or decrease over time. Consider that CQN providers may not be the lowest cost providers in your area.
             Also consider that some providers, including CQN providers, may offer discounted rates to individuals seeking care without using the CQN.
          </li>
          <li>
            <span className="font-semibold">Network Evolution:</span> Coverage, provider
            availability, and discount rates in specific ZIP codes may shift over time (improve or
            decline) as the network evolves.
          </li>
        </ul>
        <p className="text-primary-dark mt-3 text-sm leading-relaxed md:text-base">
          We recommend considering how much of the policy's projected return relies on these
          non-guaranteed savings features when comparing options.
        </p>
        <p className="text-primary-dark mt-3 text-sm leading-relaxed md:text-base">
          <span className="font-semibold">Note:</span> To remove the projected CQN savings from your
          calculations, navigate to your care cost settings and enter a custom home care rate
          instead of using the default rate.
        </p>
      </div>

      <div className="space-y-3">
        <h2 className="text-primary-dark text-lg font-semibold md:text-xl">Data Sources</h2>
        <p className="text-primary-dark text-xs italic leading-relaxed md:text-sm">
          <sup>1</sup> This savings percentage is based on information from CareScout, LLC indicating that, on
          average nationally, providers in the CareScout Quality Network offer preferred pricing
          that is 11% less than the median cost of home health care by metropolitan statistical
          area. This average is derived internally by comparing preferred CQN rates against the{' '}
          <a
            href="https://www.carescout.com/cost-of-care"
            target="_blank"
            rel="noreferrer"
            className="font-medium underline"
          >
            CareScout cost of care survey
          </a>
          . (Current as of 10/1/2025.)
        </p>
        <p className="text-primary-dark mt-2 text-xs italic leading-relaxed md:text-sm">
          <span className="font-semibold">Note on Cost Data:</span> The CareScout survey used to derive the 11% average is a different
          source from the baseline costs used generally within the Waterlily platform (which are
          sourced from the{' '}
          <a
            href="https://cdn.ltcfeds.gov/planning-tools/downloads/Cost-of-Care-Survey.pdf"
            target="_blank"
            rel="noreferrer"
            className="font-medium underline"
          >
            2021 Cost of Care Survey
          </a>{' '}
          published by the Federal Long Term Care Insurance Program{' '}
          <a
            href="https://ltcfeds.gov/"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            (LTCFEDS.gov)
          </a>
          . Waterlily models the 11% savings rate against Waterlily's estimated ZIP code costs.
        </p>
        <p className="text-primary-dark mt-2 text-xs italic leading-relaxed md:text-sm">
          <sup>2</sup> The projected median cost of care is based on median cost of care information published
          by CareScout Holdings, Inc., assuming a {inflationRatePercentage}% annual inflation rate.
        </p>
      </div>
    </div>
  ) : undefined;

  return (
    <SlideOutSheet
      open={open}
      setOpen={open => !open && onClose()}
      title="Modeled Savings"
      slideOutContent={slideOutContent}
      maxWidth="max-w-2xl"
    />
  );
}
